import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Star, CheckCircle2, Loader2, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeedbackPage() {
  const { orderId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    if (!orderId) return;
    fetch(`${BACKEND_URL}/api/public/feedback/${orderId}`)
      .then(r => r.json())
      .then(data => {
        setInfo(data);
        if (data.existing_feedback) {
          setRating(data.existing_feedback.rating || 0);
          setComment(data.existing_feedback.comment || '');
          setSubmitted(true);
        }
      })
      .catch(() => toast.error('Could not load order'))
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleSubmit = async () => {
    if (!rating) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/feedback/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Thank you for your feedback!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed to submit'); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8f7f4' }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1B2A4A' }} />
    </div>
  );

  if (!info) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8f7f4' }}>
      <p className="text-sm" style={{ color: '#6B7280' }}>Order not found.</p>
    </div>
  );

  const RATING_LABELS = ['', 'Poor', 'Below Average', 'Average', 'Very Good', 'Excellent'];

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3"
            style={{ backgroundColor: '#1B2A4A' }}>
            <span className="text-lg font-bold" style={{ color: '#C5A55A', fontFamily: '"Bodoni Moda", Georgia, serif' }}>H</span>
          </div>
          <h1 className="text-xl font-semibold" style={{ color: '#1B2A4A', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
            Herringbone & Sui
          </h1>
        </div>

        {submitted ? (
          <Card className="rounded-xl shadow-sm border-0" data-testid="feedback-submitted">
            <CardContent className="py-10 text-center space-y-3">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: '#059669' }} />
              </div>
              <h2 className="text-lg font-semibold" style={{ color: '#1B2A4A' }}>Thank You!</h2>
              <p className="text-sm" style={{ color: '#6B7280' }}>
                Your feedback for order <strong>{info.order_number}</strong> has been recorded.
              </p>
              <div className="flex items-center justify-center gap-0.5">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} className="w-6 h-6" fill={n <= rating ? '#C5A55A' : 'none'}
                    style={{ color: n <= rating ? '#C5A55A' : '#D1D5DB' }} />
                ))}
              </div>
              {comment && <p className="text-xs italic" style={{ color: '#9CA3AF' }}>"{comment}"</p>}
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-xl shadow-sm border-0" data-testid="feedback-form">
            <CardContent className="py-6 space-y-5">
              <div className="text-center">
                <p className="text-sm" style={{ color: '#6B7280' }}>How was your experience with your</p>
                <p className="text-base font-semibold mt-0.5" style={{ color: '#1B2A4A' }}>
                  {info.garment_summary || 'order'}?
                </p>
                <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
                  Order {info.order_number} · {info.client_name}
                </p>
              </div>

              {/* Star Rating */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onMouseEnter={() => setHoverRating(n)} onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(n)} className="p-1 transition-transform hover:scale-110"
                      data-testid={`feedback-star-${n}`}>
                      <Star className="w-10 h-10" strokeWidth={1.5}
                        fill={(hoverRating || rating) >= n ? '#C5A55A' : 'none'}
                        style={{ color: (hoverRating || rating) >= n ? '#C5A55A' : '#D1D5DB', transition: 'all 150ms' }} />
                    </button>
                  ))}
                </div>
                {(hoverRating || rating) > 0 && (
                  <p className="text-xs font-medium mt-1" style={{ color: '#C5A55A' }}>
                    {RATING_LABELS[hoverRating || rating]}
                  </p>
                )}
              </div>

              {/* Comment */}
              <div>
                <Textarea value={comment} onChange={e => setComment(e.target.value)}
                  placeholder="Tell us more about your experience..."
                  className="h-24 text-sm resize-none" data-testid="feedback-comment" />
              </div>

              <Button className="w-full h-11 text-sm font-medium" style={{ backgroundColor: '#1B2A4A' }}
                onClick={handleSubmit} disabled={submitting || !rating} data-testid="submit-feedback-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[10px] mt-6" style={{ color: '#D1D5DB' }}>
          Powered by Herringbone & Sui
        </p>
      </div>
    </div>
  );
}
